import JSZip from 'jszip';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  List,
  Avatar,
  Tooltip,
  ListItem,
  Checkbox,
  IconButton,
  Typography,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import fileDownload from 'src/utils/fileDownload';

import { genericApiToaster } from 'src/services/utils';

import Iconify from '../../../components/iconify';
import { getFileByName } from '../../../services/file.service';
import {
  listItemStyle,
  typographyStyle,
  attachmentListStyle,
  attachmentsHeaderStyle,
  attachmentsContainerStyle,
  attachmentsloadingButtonStyle,
} from '../styles';

const SUPPORTED_IMAGE_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.gif'];

export default function AttachmentsList({ ticketFilesList = [], ticketId = '' }) {
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();
  const { isSmallScreen } = useResponsiveScreen();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const allSelected = useMemo(
    () => selectedFiles.length === ticketFilesList.length && ticketFilesList.length > 0,
    [selectedFiles.length, ticketFilesList.length]
  );

  const hasPartialSelection = useMemo(
    () => selectedFiles.length > 0 && selectedFiles.length < ticketFilesList.length,
    [selectedFiles.length, ticketFilesList.length]
  );

  const handleCheckboxChange = useCallback((fileName) => {
    setSelectedFiles((prev) => {
      if (prev.includes(fileName)) {
        return prev.filter((file) => file !== fileName);
      }
      return [...prev, fileName];
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedFiles(allSelected ? [] : [...ticketFilesList]);
  }, [allSelected, ticketFilesList]);

  const handleDownloadFiles = useCallback(
    async (files) => {
      const filesToDownload = files.length > 0 ? files : ticketFilesList;

      if (filesToDownload.length === 0) {
        showToast({
          message: t('errorMessages.noFilesSelected'),
          severity: 'error',
          props: { hideToast },
        });
        return;
      }

      try {
        setIsDownloading(true);
        showToast({
          message: t('pages.ticketDetails.downloadWillStart'),
          severity: 'info',
          props: { hideToast },
        });

        if (filesToDownload.length === 1) {
          // Single file download
          genericApiToaster(
            getFileByName,
            filesToDownload[0],
            '',
            t('errorMessages.couldntDownloadFile'),
            showToast,
            hideToast,
            t,
            async (file) => {
              await fileDownload(file, filesToDownload[0]);
              setIsDownloading(false);
              setSelectedFiles([]);
              showToast({
                message: t('pages.ticketDetails.downloadStarted'),
                severity: 'success',
                props: { hideToast },
              });
            }
          );
        } else {
          // Multiple files download as zip
          const zip = new JSZip();
          await Promise.all(
            filesToDownload.map(async (fileName) => {
              const fileData = await getFileByName(fileName);
              zip.file(fileName, fileData);
            })
          );

          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const zipFileName = `ticket_${ticketId}_attachments_${new Date().toLocaleString()}.zip`;
          fileDownload(zipBlob, zipFileName);

          setIsDownloading(false);
          setSelectedFiles([]);
          showToast({
            message: t('pages.ticketDetails.downloadStarted'),
            severity: 'success',
            props: { hideToast },
          });
        }
      } catch (error) {
        setIsDownloading(false);
        showToast({
          message: t('errorMessages.couldntDownloadFile'),
          severity: 'error',
          props: { hideToast },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketFilesList, ticketId, t]
  );

  const getFileIcon = useCallback(
    (fileName) =>
      SUPPORTED_IMAGE_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext))
        ? 'ph:image'
        : 'bx:file',
    []
  );

  if (!ticketFilesList.length) {
    return (
      <Box display="flex" height="100px" alignItems="center" justifyContent="center">
        <Typography variant="h6">{t('pages.ticketDetails.noAttachments')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={attachmentsContainerStyle}>
      <Box
        sx={{
          ...attachmentsHeaderStyle,
          bgcolor: allSelected ? '#b4d5fe' : '#F4F6F8',
        }}
      >
        <Checkbox
          size={isSmallScreen ? 'small' : 'medium'}
          checked={allSelected}
          indeterminate={hasPartialSelection}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': t('pages.formPage.selectAll') }}
        />
        <ListItemText
          primary={
            <Typography sx={typographyStyle}>{t('pages.formPage.selectAll')}</Typography>
          }
        />
      </Box>

      <List
        sx={{
          ...attachmentListStyle,
          height: isSmallScreen ? '200px' : '350px',
          border: allSelected ? '2px solid #b4d5fe' : '2px solid transparent',
          borderRadius: allSelected && '0 0 20px 20px',
          transition: 'border-color 0.4s ease, border-width 0.4s ease',
          transitionDelay: allSelected ? '0.2s' : '0s',
        }}
        aria-label={t('pages.ticketDetails.attachmentsList')}
      >
        {ticketFilesList.map((fileName, index) => {
          const icon = getFileIcon(fileName);

          return (
            <ListItem
              key={`file-${index}-${fileName}`}
              sx={{ gap: 1 }}
              secondaryAction={
                <Tooltip title={t('pages.ticketDetails.downloadFile')}>
                  <IconButton
                    value={fileName}
                    onClick={() => handleDownloadFiles([fileName])}
                    edge="end"
                    aria-label={`${t('pages.ticketDetails.downloadFile')} ${fileName}`}
                  >
                    <Iconify
                      width={isSmallScreen ? 20 : 25}
                      icon="material-symbols:download"
                    />
                  </IconButton>
                </Tooltip>
              }
            >
              <Checkbox
                edge="start"
                size={isSmallScreen ? 'small' : 'medium'}
                checked={selectedFiles.includes(fileName)}
                onChange={() => handleCheckboxChange(fileName)}
                inputProps={{ 'aria-label': `${t('pages.formPage.select')} ${fileName}` }}
              />
              <ListItemAvatar sx={{ minWidth: 'unset', mr: 1 }}>
                <Avatar
                  sx={{
                    width: isSmallScreen ? 30 : 35,
                    height: isSmallScreen ? 30 : 35,
                  }}
                >
                  <Iconify width={isSmallScreen ? 17 : 20} icon={icon} />
                </Avatar>
              </ListItemAvatar>
              <Tooltip title={fileName}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontSize: isSmallScreen ? '0.7rem' : '1rem' }}>
                      {fileName}
                    </Typography>
                  }
                  sx={listItemStyle}
                />
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <LoadingButton
        onClick={() => handleDownloadFiles(selectedFiles)}
        variant="contained"
        loading={isDownloading}
        disabled={selectedFiles.length === 0}
        size={isSmallScreen ? 'small' : 'medium'}
        startIcon={<Iconify icon="mdi:download" />}
        sx={{
          ...attachmentsloadingButtonStyle,
          fontSize: isSmallScreen ? '0.7rem' : '1rem',
        }}
      >
        {t('buttons.downloadSlectedFiles')}
      </LoadingButton>
    </Box>
  );
}

AttachmentsList.propTypes = {
  ticketFilesList: PropTypes.array,
  ticketId: PropTypes.string,
};
